if (!customElements.get('featured-collection')) {
  customElements.define(
    'featured-collection',
    class FeaturedCollection extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.selectors = {
          sliderWrapper: '.featured-collection__items',
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
          pagination: '.swiper-pagination',
        };
        this.classes = {
          grid: 'f-grid',
          swiper: 'swiper',
          swiperWrapper: 'swiper-wrapper',
          swipeMobile: 'swipe-mobile',
          swipeMobileInner: 'swipe-mobile__inner',
        };

        this.blockId = this.dataset.blockId;
        this.block = this.closest(`[data-id="${this.blockId}"`);
        this.sliderWrapper = this.querySelector(this.selectors.sliderWrapper);

        this.enableSlider = this.dataset.enableSlider === 'true';
        this.enableMobileSlider = this.dataset.enableMobileSlider === 'true';
        this.items = parseInt(this.dataset.items) || 4;
        this.tabletItems = parseInt(this.dataset.tabletItems) || 3;
        this.laptopItems = parseInt(this.dataset.laptopItems) || 3;
        this.mobileItems = parseInt(this.dataset.mobileItems) || 2;
        this.paginationType = this.dataset.paginationType || 'bullets';
        this.peekPercent = parseInt(this.dataset.peekPercent || '0');
        this.showProgressBar = this.dataset.showProgressBar !== 'false';

        this.sliderInstance = false;

        const revealLayout = () => {
          requestAnimationFrame(() => this.removeAttribute('data-layout-stabilizing'));
        };

        if (!this.enableSlider && !this.enableMobileSlider) {
          revealLayout();
          return;
        }

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.init.bind(this);
        this.init();
        revealLayout();
      }

      init() {
        if (this.sliderInstance) {
          this.destroySlider();
        }
        this.initSlider(FoxTheme.config.mqlMobile);
      }

      initSlider(isMobile = false) {
        const columnGap = window.getComputedStyle(this.sliderWrapper).getPropertyValue('--f-column-gap');
        // --f-column-gap is only defined under .f-grid; in slider mode the wrapper may not
        // carry that class, so the var can be empty and NaN here breaks Swiper slide sizing.
        const parsedGap = parseFloat(columnGap.replace('rem', '')) * 10;
        const spaceBetween = Number.isFinite(parsedGap) ? parsedGap : 20;

        this.sliderOptions = {
          slidesPerView: 2,
          spaceBetween: spaceBetween,
          navigation: {
            nextEl: this.block.querySelector(this.selectors.nextEl),
            prevEl: this.block.querySelector(this.selectors.prevEl),
          },
          pagination: false,
          breakpoints: {
            768: {
              slidesPerView: this.tabletItems + this.peekPercent / 100,
            },
            1024: {
              slidesPerView: this.laptopItems + this.peekPercent / 100,
            },
            1280: {
              slidesPerView: this.items + this.peekPercent / 100,
            },
          },
          loop: false,
          threshold: 2,
          watchSlidesProgress: true,
          mousewheel: {
            enabled: true,
            forceToAxis: true,
          },
        };

        if (isMobile) {
          this.sliderOptions.slidesPerView = this.mobileItems + this.peekPercent / 100;
          this.sliderOptions.breakpoints = {};
          this.sliderOptions.mousewheel = false;
          this.sliderOptions.navigation = false;
          this.sliderOptions.spaceBetween = spaceBetween || 10;
          if (!this.showProgressBar) {
            this.sliderOptions.pagination = false;
          }
        } else if (!this.enableSlider) {
          // Mobile-only mode: destroy on desktop
          return;
        }

        // Remove swipe-mobile classes as Swiper takes over
        this.classList.remove(this.classes.swipeMobile);
        const swipeMobileClasses = [...this.classList].filter(c => c.startsWith('swipe-mobile--'));
        swipeMobileClasses.forEach(c => this.classList.remove(c));
        if (this.sliderWrapper) {
          this.sliderWrapper.classList.remove(this.classes.swipeMobileInner);
        }

        this.classList.add(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.grid);
        this.sliderWrapper.classList.add(this.classes.swiperWrapper);

        // Add swiper-slide to children if not already present (mobile-only mode)
        Array.from(this.sliderWrapper.children).forEach((child) => {
          if (!child.classList.contains('swiper-slide')) {
            child.classList.add('swiper-slide');
          }
        });

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, [FoxTheme.Swiper.Mousewheel]);
        this.sliderInstance.init();

        this.initCondensedPagination(isMobile);

        if (isMobile && !this.showProgressBar) {
          const paginationEl = this.block?.querySelector(this.selectors.pagination);
          if (paginationEl) {
            paginationEl.innerHTML = '';
          }
        }

        // const focusableElements = FoxTheme.a11y.getFocusableElements(this);

        // focusableElements.forEach((element) => {
        //   element.addEventListener('focusin', () => {
        //     const slide = element.closest('.swiper-slide');
        //     this.sliderInstance && this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
        //   });
        // });

        this.calcNavButtonsPosition();
        this.fixQuickviewDuplicate();
      }

      destroySlider() {
        this.classList.remove(this.classes.swiper);
        this.sliderWrapper.classList.remove(this.classes.swiperWrapper);
        this.sliderWrapper.classList.add(this.classes.grid);

        // Remove swiper-slide class from children in mobile-only mode
        if (!this.enableSlider) {
          Array.from(this.sliderWrapper.children).forEach((child) => {
            child.classList.remove('swiper-slide');
          });
        }

        if (typeof this.sliderInstance !== 'object') return;
        this.sliderInstance.slider.destroy();
        this.sliderInstance = false;

        // Restore swipe-mobile classes for mobile-only mode
        if (this.enableMobileSlider && !this.enableSlider) {
          this.classList.add(this.classes.swipeMobile);
          this.classList.add('swipe-mobile--' + this.mobileItems + '-cols');
          if (this.sliderWrapper) {
            this.sliderWrapper.classList.add(this.classes.swipeMobileInner);
          }
        }
      }

      fixQuickviewDuplicate() {
        let modalIds = [];
        Array.from(this.querySelectorAll('quick-view-modal')).forEach((modal) => {
          const modalID = modal.getAttribute('id');
          if (modalIds.includes(modalID)) {
            modal.remove();
          } else {
            modalIds.push(modalID);
          }
        });
      }

      calcNavButtonsPosition() {
        if (this.dataset.navigationPosition !== 'center_image') return;

        const firstMedia = this.querySelector('.product-card__image-wrapper');
        const controls = this.block.querySelector('.swiper-controls');
        if (controls && firstMedia && firstMedia.clientHeight > 0) {
          controls.style.setProperty('--swiper-navigation-top-offset', parseInt(firstMedia.clientHeight) / 2 + 'px');
        }
      }

      initCondensedPagination(isMobile = false) {
        const paginationEl = this.block?.querySelector(this.selectors.pagination);
        const slider = this.sliderInstance?.slider;
        if (!paginationEl || !slider) return;

        if (isMobile && !this.showProgressBar) {
          paginationEl.innerHTML = '';
          return;
        }

        const totalSlides = Math.max(1, slider.slides.length);
        if (totalSlides <= 1) {
          paginationEl.innerHTML = '';
          return;
        }

        const dotCount = Math.min(4, totalSlides);
        paginationEl.innerHTML = '';
        paginationEl.classList.add('swiper-pagination-bullets', 'swiper-pagination-horizontal');

        const dots = [];
        const getTargetIndex = (dotIndex) => {
          if (dotCount === 1) return 0;
          return Math.round(((totalSlides - 1) * dotIndex) / (dotCount - 1));
        };

        const updateActive = () => {
          const activeSlide = Math.max(0, slider.activeIndex || 0);
          const activeDot = dotCount === 1 ? 0 : Math.round((activeSlide / Math.max(1, totalSlides - 1)) * (dotCount - 1));
          dots.forEach((dot, index) => {
            dot.classList.toggle('swiper-pagination-bullet-active', index === activeDot);
          });
        };

        for (let i = 0; i < dotCount; i++) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'swiper-pagination-bullet';
          dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
          dot.addEventListener('click', () => {
            slider.slideTo(getTargetIndex(i));
          });
          paginationEl.appendChild(dot);
          dots.push(dot);
        }

        slider.on('slideChange', updateActive);
        updateActive();
      }
    }
  );
}
